import type { Booking } from '../types/booking';

/**
 * What a booking is to the advertiser, in their words. Derived from the
 * backend status plus payment state so every advertiser page (board, list,
 * Home, booking page) sorts and labels bookings the same way.
 */
export type AdvertiserBookingState =
    | 'waiting'    // Pending: the owner has not answered yet
    | 'readyToPay' // Approved and unpaid
    | 'scheduled'  // paid (or no prepayment needed), starts later
    | 'live'       // Active
    | 'finished'   // Completed, or the end date has passed
    | 'declined'   // Rejected by the owner
    | 'cancelled'; // Cancelled or expired

export interface AdvertiserBookingStateInfo {
    label: string;
    description: string;
    color: 'default' | 'primary' | 'error' | 'warning' | 'success';
    /** Hatched on the schedule board: the wait is on someone else. */
    hatched?: boolean;
}

export const ADVERTISER_BOOKING_STATE: Record<AdvertiserBookingState, AdvertiserBookingStateInfo> = {
    waiting: { label: 'Waiting for owner', description: 'The screen owner has not answered this request yet', color: 'warning', hatched: true },
    readyToPay: { label: 'Ready to pay', description: 'The owner approved. Pay to confirm the dates', color: 'warning' },
    scheduled: { label: 'Scheduled', description: 'Confirmed and starts on the first booked day', color: 'primary' },
    live: { label: 'Live', description: 'Playing on the screen now', color: 'success' },
    finished: { label: 'Finished', description: 'The booked days have ended', color: 'default' },
    declined: { label: 'Declined', description: 'The owner declined this request', color: 'error' },
    cancelled: { label: 'Cancelled', description: 'This booking was cancelled or expired unpaid', color: 'default' },
};

export function advertiserBookingState(
    booking: Pick<Booking, 'status' | 'paymentStatus' | 'endDate'> & { totalPrice?: number },
    now: Date = new Date(),
): AdvertiserBookingState {
    const ended = new Date(booking.endDate + 'T23:59:59') < now;
    switch (booking.status) {
        case 'Active':
            return ended ? 'finished' : 'live';
        case 'Completed':
            return 'finished';
        case 'Rejected':
            return 'declined';
        case 'Cancelled':
        case 'Draft':
            return 'cancelled';
        case 'Pending':
            return ended ? 'cancelled' : 'waiting';
        case 'Approved': {
            if (ended) return 'finished';
            // Approval opens the payment window: None (not started) and
            // OrderCreated (checkout opened) both still owe; Captured is paid.
            if (booking.paymentStatus === 'Captured') return 'scheduled';
            if (booking.paymentStatus === 'Expired') return 'cancelled';
            // Nothing to pay: the approval is the confirmation.
            if (booking.totalPrice != null && booking.totalPrice <= 0) return 'scheduled';
            return 'readyToPay';
        }
        default:
            return 'scheduled';
    }
}

/** The order the board and the filter chips use: what needs the advertiser first. */
export const ADVERTISER_STATE_ORDER: AdvertiserBookingState[] = [
    'readyToPay', 'waiting', 'live', 'scheduled', 'finished', 'declined', 'cancelled',
];

export function needsAdvertiser(state: AdvertiserBookingState): boolean {
    return state === 'readyToPay';
}
